import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, PenLine, Sparkles, FileText, Trash2, Loader2, CheckCircle2, Pencil, Check, X, BookOpen, ExternalLink, ChevronDown, ChevronUp, Download } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const DURATION_LABEL = {
    "60s": "60 seconds",
    "90s": "90 seconds",
    "2m30": "2 minutes 30 seconds",
    "3m": "3 minutes",
};

const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [project, setProject] = useState(null);
    const [scripts, setScripts] = useState([]);
    const [research, setResearch] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [expandedResearch, setExpandedResearch] = useState(null);

    const startRename = () => {
        setRenameValue(project?.name || "");
        setRenaming(true);
    };

    const cancelRename = () => {
        setRenaming(false);
        setRenameValue("");
    };

    const saveRename = async () => {
        const trimmed = renameValue.trim();
        if (!trimmed) {
            toast.error("Name cannot be empty");
            return;
        }
        if (trimmed === project.name) {
            setRenaming(false);
            return;
        }
        setSavingName(true);
        try {
            const { data } = await api.patch(`/projects/${id}`, { name: trimmed });
            setProject((p) => ({ ...p, name: data.name }));
            toast.success("Project renamed");
            setRenaming(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Rename failed");
        } finally {
            setSavingName(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: p }, { data: s }, { data: r }] = await Promise.all([
                api.get(`/projects/${id}`),
                api.get(`/projects/${id}/scripts`),
                api.get(`/projects/${id}/research`).catch(() => ({ data: [] })),
            ]);
            setProject(p);
            setScripts(s);
            setResearch(r || []);
        } catch (e) {
            toast.error("Failed to load project");
            navigate("/dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/scripts/generate", { project_id: id });
            const first = data.scripts?.[0];
            const count = data.scripts?.length || 0;
            toast.success(`${count} script variants generated`);
            if (first) navigate(`/scripts/${first.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    const deleteBatch = async (batchScripts) => {
        const count = batchScripts.length;
        const ok = await confirm({
            title: "Delete Generation",
            message: `Delete all ${count} script variant${count === 1 ? "" : "s"} in this generation? This can’t be undone.`,
            confirmLabel: count === 1 ? "Delete Script" : `Delete ${count} Variants`,
            cancelLabel: "Keep Them",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await Promise.all(batchScripts.map((s) => api.delete(`/scripts/${s.id}`)));
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const deleteResearch = async (rid) => {
        const ok = await confirm({
            title: "Delete Research",
            message: "Delete this saved research session? This can't be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Keep It",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/projects/${id}/research/${rid}`);
            toast.success("Research deleted");
            setResearch((r) => r.filter((x) => x.id !== rid));
        } catch {
            toast.error("Delete failed");
        }
    };

    const downloadResearchMd = (r) => {
        const stamp = new Date(r.created_at || Date.now()).toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const lines = [
            `# UnCloned® Research — ${r.field_label || r.field_key}`,
            "",
            `**Saved:** ${new Date(r.created_at).toLocaleString()}`,
        ];
        if (r.topic) lines.push(`**Video topic:** ${r.topic}`);
        if (r.hint) lines.push(`**Hint used:** ${r.hint}`);
        lines.push("", "---", "", "## Drafts", "");
        (r.options || []).forEach((o, i) => lines.push(`### Option ${i + 1}`, "", o, ""));
        lines.push("---", "", "## Sources", "");
        (r.sources || []).forEach((s, i) => {
            lines.push(`### [${i + 1}] ${s.title || "Untitled"}`);
            lines.push(`**URL:** ${s.url}`);
            lines.push("");
            if (s.snippet) lines.push(`> ${s.snippet.replace(/\n/g, "\n> ")}`, "");
        });
        const md = lines.join("\n") + "\n";
        const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `uncloned-research-${r.field_key}-${stamp}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    // Group scripts into generation batches
    const batches = useMemo(() => {
        const map = new Map();
        for (const s of scripts) {
            const key = s.batch_id || s.id;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        // Sort variants within batch by variant letter, batches by newest first (max version)
        const list = Array.from(map.entries()).map(([key, arr]) => {
            arr.sort((a, b) => (a.variant || "").localeCompare(b.variant || "") || a.version - b.version);
            return { key, scripts: arr, maxVersion: Math.max(...arr.map((s) => s.version)) };
        });
        list.sort((a, b) => b.maxVersion - a.maxVersion);
        return list;
    }, [scripts]);

    if (loading || !project) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="project-loading">
                <Loader2 className="w-6 h-6 animate-spin text-un-berry" />
            </div>
        );
    }

    const intake = project.intake || {};
    const requiredKeys = [
        "video_purpose",
        "video_platforms",
        "video_topic",
        "duration",
        "video_pov",
        "video_myth",
        "video_belief_shift",
        "desired_cta",
    ];
    const hasIntakeValue = (v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === "string") return v.trim().length > 0;
        if (Array.isArray(v)) return v.length > 0;
        return Boolean(v);
    };
    const missing = requiredKeys.filter((k) => !hasIntakeValue(intake[k]));
    const intakeComplete = missing.length === 0;

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="project-detail">
            <button
                onClick={() => navigate("/dashboard")}
                className="un-btn-ghost flex items-center gap-2 text-sm mb-6"
                data-testid="project-back"
            >
                <ArrowLeft className="w-4 h-4" /> All projects
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8">
                    <div className="un-label text-un-grape mb-3">Project</div>
                    {renaming ? (
                        <div className="flex items-start gap-3 mb-4" data-testid="project-rename-form">
                            <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") saveRename();
                                    if (e.key === "Escape") cancelRename();
                                }}
                                autoFocus
                                className="un-input un-h1 !text-4xl md:!text-5xl !py-2 !px-3 flex-1"
                                data-testid="project-rename-input"
                            />
                            <div className="flex flex-col gap-2 pt-2">
                                <button
                                    onClick={saveRename}
                                    disabled={savingName}
                                    aria-label="Save name"
                                    className="un-btn-primary !px-3 !py-2"
                                    data-testid="project-rename-save"
                                >
                                    {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                </button>
                                <button
                                    onClick={cancelRename}
                                    aria-label="Cancel rename"
                                    className="un-btn-outline !px-3 !py-2"
                                    data-testid="project-rename-cancel"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start gap-3 mb-4 group">
                            <h1 className="un-h1 text-5xl md:text-6xl" data-testid="project-title">
                                {project.name}
                            </h1>
                            <button
                                onClick={startRename}
                                aria-label="Rename project"
                                className="un-btn-ghost !px-2 !py-2 mt-2 text-un-onyx/40 hover:!text-un-berry opacity-70 hover:opacity-100"
                                data-testid="project-rename-button"
                                title="Rename project"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-8">
                        {project.duration && <span className="un-pill un-pill-berry">{DURATION_LABEL[project.duration] || project.duration}</span>}
                        {project.tone && <span className="un-pill un-pill-outline">{project.tone}</span>}
                        {intakeComplete ? (
                            <span className="un-pill un-pill-lime" data-testid="intake-status-complete">Intake complete</span>
                        ) : (
                            <span className="un-pill un-pill-outline" data-testid="intake-status-incomplete">Intake incomplete</span>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-3 mb-4">
                        <Link
                            to={`/projects/${id}/intake`}
                            className="un-btn-outline flex items-center gap-2"
                            data-testid="project-edit-intake"
                        >
                            <PenLine className="w-4 h-4" /> {intakeComplete ? "Edit intake & regenerate" : "Complete intake"}
                        </Link>
                        <button
                            onClick={generate}
                            disabled={!intakeComplete || generating}
                            className="un-btn-primary flex items-center gap-2"
                            data-testid="project-generate"
                        >
                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {generating ? "Writing 3 variants…" : batches.length > 0 ? "Generate new variants" : "Generate 3 variants"}
                        </button>
                    </div>
                    <p className="text-sm text-un-onyx/60 max-w-2xl mb-10 flex items-start gap-2" data-testid="autosave-hint">
                        <CheckCircle2 className="w-4 h-4 text-un-berry mt-0.5 flex-shrink-0" />
                        Each generation produces 3 variants (A / B / C). Every variant is auto-saved. Regenerating never overwrites. Earlier variants stay here as history.
                    </p>

                    <div className="un-divider pt-8">
                        <div className="un-label text-un-grape mb-4">
                            Generations ({batches.length})
                        </div>
                        {batches.length === 0 ? (
                            <div className="un-card p-8 text-center" data-testid="scripts-empty">
                                <FileText className="w-8 h-8 mx-auto mb-3 text-un-berry" />
                                <div className="un-h3 mb-1">No scripts yet</div>
                                <div className="text-un-onyx/60 text-sm">
                                    Complete the intake, then generate your first set of 3 variants.
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6" data-testid="scripts-list">
                                {batches.map((batch, batchIdx) => (
                                    <div key={batch.key} className="border-2 border-un-onyx p-5" data-testid={`batch-${batch.key}`}>
                                        <div className="flex items-center justify-between gap-3 mb-4">
                                            <div className="flex items-center gap-2">
                                                <span className="un-pill un-pill-onyx">
                                                    Generation #{batches.length - batchIdx}
                                                </span>
                                                <span className="un-mono text-xs text-un-onyx/50">
                                                    {new Date(batch.scripts[0].created_at).toLocaleString()}
                                                </span>
                                            </div>
                                            {batch.scripts.length > 1 && (
                                                <button
                                                    onClick={() => deleteBatch(batch.scripts)}
                                                    className="un-btn-ghost !px-2 text-xs text-un-onyx/40 hover:!text-un-berry flex items-center gap-1"
                                                    data-testid={`batch-delete-${batch.key}`}
                                                >
                                                    <Trash2 className="w-3 h-3" /> Delete all
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            {batch.scripts.map((s) => (
                                                <Link
                                                    to={`/scripts/${s.id}`}
                                                    key={s.id}
                                                    className="border-2 border-un-onyx bg-white p-4 un-card-hover"
                                                    data-testid={`script-item-${s.id}`}
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="un-pill un-pill-berry">
                                                            Variant {s.variant || "•"}
                                                        </span>
                                                        <span className="un-mono text-xs text-un-onyx/60">
                                                            {s.word_count}w
                                                        </span>
                                                    </div>
                                                    <div className="un-h3 text-base leading-snug line-clamp-3 mb-2">
                                                        {s.title}
                                                    </div>
                                                    <div className="un-mono text-xs text-un-onyx/50">
                                                        V{s.version} · {DURATION_LABEL[s.duration] || s.duration}
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {research.length > 0 && (
                        <div className="un-divider pt-8 mt-10" data-testid="research-library">
                            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                                <div className="un-label text-un-grape flex items-center gap-2">
                                    <BookOpen className="w-4 h-4" />
                                    Research Library ({research.length})
                                </div>
                                <div className="un-mono text-[0.65rem] text-un-onyx/60">
                                    Saved via the AI Assistant on the stats field
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                                {research.map((r) => {
                                    const isOpen = expandedResearch === r.id;
                                    return (
                                        <div
                                            key={r.id}
                                            className="border-2 border-un-onyx bg-white"
                                            data-testid={`research-item-${r.id}`}
                                        >
                                            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b-2 border-un-onyx/20 flex-wrap">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="un-pill un-pill-berry text-[0.6rem]">
                                                        {r.field_label || r.field_key}
                                                    </span>
                                                    <span className="un-mono text-xs text-un-onyx/50">
                                                        {new Date(r.created_at).toLocaleString()}
                                                    </span>
                                                    {r.topic && (
                                                        <span className="text-xs text-un-onyx/70 line-clamp-1 max-w-md">
                                                            · {r.topic}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => downloadResearchMd(r)}
                                                        className="un-btn-ghost !px-2 text-xs inline-flex items-center gap-1"
                                                        data-testid={`research-download-${r.id}`}
                                                        title="Download as markdown"
                                                    >
                                                        <Download className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={() => setExpandedResearch(isOpen ? null : r.id)}
                                                        className="un-btn-ghost !px-2 text-xs inline-flex items-center gap-1"
                                                        data-testid={`research-toggle-${r.id}`}
                                                    >
                                                        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                        {isOpen ? "Hide" : "Show"}
                                                    </button>
                                                    <button
                                                        onClick={() => deleteResearch(r.id)}
                                                        className="un-btn-ghost !px-2 text-xs text-un-onyx/40 hover:!text-un-berry inline-flex items-center gap-1"
                                                        data-testid={`research-delete-${r.id}`}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                            {isOpen && (
                                                <div className="px-4 py-4 space-y-4">
                                                    {r.hint && (
                                                        <div className="text-xs">
                                                            <span className="un-mono text-[0.6rem] font-bold text-un-grape">HINT · </span>
                                                            <span className="text-un-onyx/80">{r.hint}</span>
                                                        </div>
                                                    )}
                                                    {(r.options || []).length > 0 && (
                                                        <div>
                                                            <div className="un-mono text-[0.65rem] font-bold text-un-berry mb-2">DRAFT OPTIONS</div>
                                                            <div className="space-y-2">
                                                                {r.options.map((o, i) => (
                                                                    <div key={i} className="border-2 border-un-onyx/20 bg-un-lime/20 px-3 py-2 text-sm text-un-onyx whitespace-pre-wrap">
                                                                        <span className="un-mono text-[0.65rem] font-bold text-un-grape mr-2">OPT {i + 1}</span>
                                                                        {o}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(r.sources || []).length > 0 && (
                                                        <div>
                                                            <div className="un-mono text-[0.65rem] font-bold text-un-berry mb-2">SOURCES</div>
                                                            <div className="space-y-2">
                                                                {r.sources.map((s, i) => (
                                                                    <a
                                                                        key={s.url || i}
                                                                        href={s.url}
                                                                        target="_blank"
                                                                        rel="noreferrer noopener"
                                                                        className="block border-2 border-un-onyx/40 p-2 hover:bg-un-water/40 transition-colors"
                                                                        data-testid={`research-source-${r.id}-${i + 1}`}
                                                                    >
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <span className="un-mono text-[0.6rem] font-bold text-un-berry">[{i + 1}]</span>
                                                                            <span className="text-sm font-bold text-un-onyx line-clamp-1 flex-1">{s.title || s.url}</span>
                                                                            <ExternalLink className="w-3 h-3 text-un-onyx/50 flex-shrink-0" />
                                                                        </div>
                                                                        <div className="un-mono text-[0.6rem] text-un-onyx/60 break-all">{s.url}</div>
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-4">
                    <div className="un-card p-6" data-testid="intake-summary">
                        <div className="un-label text-un-grape mb-4">Intake snapshot</div>
                        {Object.keys(intake).length === 0 ? (
                            <p className="text-un-onyx/60 text-sm">
                                Start the intake to fill in your positioning, offer, buyer and CTA.
                            </p>
                        ) : (
                            <dl className="space-y-4 text-sm">
                                {[
                                    ["video_topic", "Video Topic"],
                                    ["video_purpose", "Purpose"],
                                    ["video_platforms", "Platforms"],
                                    ["video_pov", "Point Of View"],
                                    ["video_myth", "Myth Challenged"],
                                    ["video_belief_shift", "Belief Shift"],
                                    ["desired_cta", "CTA"],
                                    ["communication_style", "Voice"],
                                ].map(([k, label]) => {
                                    const raw = intake[k];
                                    const val = Array.isArray(raw) ? raw.join(", ") : raw;
                                    return (
                                        <div key={k}>
                                            <dt className="un-label text-un-grape text-[0.6rem]">{label}</dt>
                                            <dd className="text-un-onyx mt-1 line-clamp-3 whitespace-pre-wrap">
                                                {val || <span className="text-un-onyx/40">— not set —</span>}
                                            </dd>
                                        </div>
                                    );
                                })}
                            </dl>
                        )}
                        <div className="un-divider mt-6 pt-5">
                            <Link
                                to={`/projects/${id}/intake`}
                                className="un-btn-outline text-sm w-full text-center block"
                                data-testid="summary-edit-intake"
                            >
                                {intakeComplete ? "Edit intake" : "Open full intake"}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;
